import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { Section, TextField, TextArea, ToggleSwitch } from './FormFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholder templates shown per-language so the admin can see what will be
// sent if they leave a field blank. Mirrors the hardcoded backend map in
// backend/services/email_service.py — kept as static strings on purpose;
// changes here are cosmetic hints only, the backend still owns the true default.
const TEMPLATE_HINTS = {
  en: {
    subject: 'Thanks for contacting English Passion, {name}',
    greeting: 'Hi {name},',
    body: "Thanks for reaching out to English Passion! We've received your message and a member of our team will get back to you within 24 hours.",
    closing: 'In the meantime, feel free to explore our programs and student stories.',
    yourMessageLabel: 'Your message',
    signature: '— The English Passion Team',
  },
  ja: {
    subject: 'English Passionへのお問い合わせありがとうございます、{name}様',
    greeting: '{name}様',
    body: 'English Passionにお問い合わせいただきありがとうございます。メッセージを受け付けました。24時間以内にチームより返信いたします。',
    closing: 'それまでの間、私たちのプログラムや生徒さんのストーリーもぜひご覧ください。',
    yourMessageLabel: 'お送りいただいた内容',
    signature: '— English Passionチーム',
  },
  ko: {
    subject: 'English Passion에 문의해 주셔서 감사합니다, {name}님',
    greeting: '{name}님,',
    body: 'English Passion에 문의해 주셔서 감사합니다. 메시지를 잘 받았으며, 24시간 이내에 담당자가 답변드리겠습니다.',
    closing: '그동안 저희 프로그램과 수강생 이야기도 편하게 살펴봐 주세요.',
    yourMessageLabel: '보내주신 메시지',
    signature: '— English Passion 팀',
  },
  vi: {
    subject: 'Cảm ơn bạn đã liên hệ với English Passion, {name}',
    greeting: 'Xin chào {name},',
    body: 'Cảm ơn bạn đã liên hệ với English Passion! Chúng tôi đã nhận được tin nhắn của bạn và đội ngũ sẽ phản hồi trong vòng 24 giờ.',
    closing: 'Trong thời gian chờ đợi, bạn có thể tham khảo các chương trình và câu chuyện học viên của chúng tôi.',
    yourMessageLabel: 'Nội dung bạn đã gửi',
    signature: '— Đội ngũ English Passion',
  },
  pt: {
    subject: 'Obrigado por contactar a English Passion, {name}',
    greeting: 'Olá {name},',
    body: 'Obrigado por contactar a English Passion! Recebemos a sua mensagem e um membro da equipa irá responder em até 24 horas.',
    closing: 'Entretanto, sinta-se à vontade para explorar os nossos programas e histórias de alunos.',
    yourMessageLabel: 'A sua mensagem',
    signature: '— A equipa da English Passion',
  },
  it: {
    subject: 'Grazie per averci contattato, {name}',
    greeting: 'Ciao {name},',
    body: 'Grazie per aver contattato English Passion! Abbiamo ricevuto il tuo messaggio e un membro del nostro team ti risponderà entro 24 ore.',
    closing: "Nel frattempo, dai un'occhiata ai nostri programmi e alle storie dei nostri studenti.",
    yourMessageLabel: 'Il tuo messaggio',
    signature: '— Il team di English Passion',
  },
  ru: {
    subject: 'Спасибо, что связались с English Passion, {name}',
    greeting: 'Здравствуйте, {name}!',
    body: 'Спасибо, что написали в English Passion! Мы получили ваше сообщение, и наша команда ответит вам в течение 24 часов.',
    closing: 'А пока приглашаем вас познакомиться с нашими программами и историями студентов.',
    yourMessageLabel: 'Ваше сообщение',
    signature: '— Команда English Passion',
  },
  th: {
    subject: 'ขอบคุณที่ติดต่อ English Passion, {name}',
    greeting: 'สวัสดีคุณ {name},',
    body: 'ขอบคุณที่ติดต่อ English Passion! เราได้รับข้อความของคุณแล้ว และทีมงานจะติดต่อกลับภายใน 24 ชั่วโมง',
    closing: 'ระหว่างนี้ ขอเชิญคุณสำรวจโปรแกรมและเรื่องราวของนักเรียนของเราได้ตามสบาย',
    yourMessageLabel: 'ข้อความของคุณ',
    signature: '— ทีม English Passion',
  },
};

const getHint = (code, field) => (TEMPLATE_HINTS[code] || TEMPLATE_HINTS.en)[field] || TEMPLATE_HINTS.en[field];

const LanguageCard = ({ lang, cfg, update }) => {
  const [open, setOpen] = useState(false);
  const code = (lang.code || '').toLowerCase();
  const senderEmail = cfg.senderEmails?.[code] || '';
  const ctaLabel = cfg.ctaLabel?.[code] || '';
  const tpl = cfg.templates?.[code] || {};

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`autoreply-lang-${code}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
        data-testid={`autoreply-lang-toggle-${code}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-900">
            {lang.name}
            {lang.nativeName && lang.nativeName !== lang.name ? ` (${lang.nativeName})` : ''}
          </span>
          <span className="text-xs text-slate-400 uppercase tracking-wider">{code}</span>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <div className="p-4 pt-0 border-t border-slate-100 space-y-4">
          <TextField
            label="Sender Email (From)"
            value={senderEmail}
            onChange={(v) => update(['senderEmails', code], v)}
            placeholder="Falls back to Default below if left blank"
            testId={`autoreply-sender-email-${code}`}
          />
          <TextField
            label="CTA Button Label"
            value={ctaLabel}
            onChange={(v) => update(['ctaLabel', code], v)}
            placeholder={getHint(code, 'body') ? (TEMPLATE_HINTS[code]?.body ? '' : '') : ''}
            testId={`autoreply-cta-label-${code}`}
          />
          <TextField
            label="Subject"
            value={tpl.subject || ''}
            onChange={(v) => update(['templates', code, 'subject'], v)}
            placeholder={getHint(code, 'subject')}
            testId={`autoreply-tpl-subject-${code}`}
          />
          <TextField
            label="Greeting"
            value={tpl.greeting || ''}
            onChange={(v) => update(['templates', code, 'greeting'], v)}
            placeholder={getHint(code, 'greeting')}
            testId={`autoreply-tpl-greeting-${code}`}
          />
          <TextArea
            label="Body"
            value={tpl.body || ''}
            onChange={(v) => update(['templates', code, 'body'], v)}
            rows={3}
          />
          <TextArea
            label="Closing"
            value={tpl.closing || ''}
            onChange={(v) => update(['templates', code, 'closing'], v)}
            rows={2}
          />
          <TextField
            label="'Your message' Label"
            value={tpl.yourMessageLabel || ''}
            onChange={(v) => update(['templates', code, 'yourMessageLabel'], v)}
            placeholder={getHint(code, 'yourMessageLabel')}
            testId={`autoreply-tpl-your-msg-${code}`}
          />
          <TextField
            label="Signature"
            value={tpl.signature || ''}
            onChange={(v) => update(['templates', code, 'signature'], v)}
            placeholder={getHint(code, 'signature')}
            testId={`autoreply-tpl-signature-${code}`}
          />
          <p className="text-xs text-slate-400 italic">
            Tip: You can use <code>{'{name}'}</code> in the Subject and Greeting to insert the sender&apos;s name. Leave any field blank to use the default shown as a placeholder.
          </p>
        </div>
      )}
    </div>
  );
};

const AutoReplyTab = ({ content, updateAutoReply, updateAutoReplyPath }) => {
  const [languages, setLanguages] = useState([]);
  const cfg = content.autoReply || {};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/languages`);
        if (!cancelled) setLanguages(res.data.languages || []);
      } catch (err) {
        if (!cancelled) setLanguages([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6">
      <Section
        title="Auto-Reply Email"
        description="Configure the confirmation email that is automatically sent to anyone who submits the Contact form. The reply is delivered in the visitor's preferred communication language."
      >
        <div className="flex items-center gap-2 text-slate-700 mb-2">
          <Mail className="w-4 h-4" /> <span className="text-sm">Global settings</span>
        </div>
        <ToggleSwitch
          label="Auto-reply enabled"
          checked={cfg.enabled !== false}
          onChange={(v) => updateAutoReply('enabled', v)}
        />
        <TextField
          label="From Name (display name in inbox)"
          value={cfg.senderName || ''}
          onChange={(v) => updateAutoReply('senderName', v)}
          placeholder="English Passion Team"
          testId="autoreply-sender-name"
        />
        <TextField
          label="Default Sender Email"
          value={cfg.senderEmails?.default || ''}
          onChange={(v) => updateAutoReplyPath(['senderEmails', 'default'], v)}
          placeholder="Used when no per-language sender is set"
          testId="autoreply-sender-default"
        />
        <TextField
          label="CTA URL (shown in every reply)"
          value={cfg.ctaUrl || ''}
          onChange={(v) => updateAutoReply('ctaUrl', v)}
          placeholder="https://englishpassion.uk/contact"
          testId="autoreply-cta-url"
        />
        <p className="text-xs text-slate-500">
          A CTA button appears in every auto-reply when both the URL above and a per-language button label below are set. Leave the URL blank to hide the button.
        </p>
      </Section>

      <Section
        title="Per-language Templates"
        description="Set the sender email, CTA label, subject and body for each language. Placeholders show the default that will be used if a field is left blank."
      >
        {languages.length === 0 && (
          <p className="text-sm text-slate-400 italic">Loading languages…</p>
        )}
        <div className="space-y-3">
          {languages.map((lang) => (
            <LanguageCard
              key={lang.code}
              lang={lang}
              cfg={cfg}
              update={updateAutoReplyPath}
            />
          ))}
        </div>
      </Section>
    </div>
  );
};

export default AutoReplyTab;
